import CheckIcon from "@mui/icons-material/CheckRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditIcon from "@mui/icons-material/EditOutlined";
import LinkIcon from "@mui/icons-material/InsertLinkRounded";
import { Button, Card, CardMedia, Container, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../api/adminApi";
import type { VideoLibraryItem } from "../../../api/types/videoLibraryTypes";
import useClientContext from "../../../context/useClientContext";
import useUserContext from "../../../context/useUserContext";
import { pageRoutes } from "../../../routes";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import DeleteVideoDialog from "./DeleteVideoDialog";
import EditVideoDialog from "./EditVideoDialog";
import VideoInfoSection from "./VideoInfoSection";

interface DialogState {
  openDialog?: "edit" | "delete";
}

const VideoPage = () => {
  usePageTitle("Video");

  const { id } = useParams();
  const { isEntriliaUser } = useUserContext();
  const { clientCode, hasPermissions } = useClientContext();
  const navigate = useNavigate();

  const [dialogState, setDialogState] = useState<DialogState>({});
  const [isLinkJustCopied, setIsLinkJustCopied] = useState(false);

  const getVideoInfo = useCallback(() => adminApi.getVideoItemFromLibrary(id || ""), [id]);
  const [videoInfo, fetchVideoError, { setData: setVideoInfo }] = useFetch(getVideoInfo);

  const [categories, fetchCategoriesError] = useFetch(adminApi.getVideoLibraryCategories);

  const isFileInfoFetchDisabled = !id || !videoInfo;
  const getFileInfo = useCallback(
    () =>
      adminApi.getEntityFileDownloadInfo(
        "PlatformObject",
        defined(id),
        "VideoLibraryItem",
        defined(videoInfo).dataCatalogId
      ),
    [id, videoInfo]
  );

  const [fileInfo, fetchFileInfoError] = useFetch(getFileInfo, { disabled: isFileInfoFetchDisabled });

  if (!id) {
    return <Navigate to=".." />;
  }

  const fetchError = fetchVideoError || fetchCategoriesError || fetchFileInfoError;
  if (fetchError) {
    logError(fetchError, "[VideoPage] fetch");
    return <DataLoadingFailed />;
  }

  if (videoInfo === undefined || categories === undefined || fileInfo === undefined) {
    return <InlineLoader />;
  }

  const hasPermissionsToManageVideos = isEntriliaUser && hasPermissions(["ManageOrganizationVideoLibrary"]);

  const handleUpdated = (updatedItem: VideoLibraryItem) => {
    setVideoInfo(updatedItem);
  };

  const handleDeleted = () => {
    navigate("..");
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/${clientCode}/${pageRoutes.videoLibrary}/${id}`);
    setIsLinkJustCopied(true);
    setTimeout(() => setIsLinkJustCopied(false), 2000);
  };

  return (
    <>
      <GeneralPageHeader title={videoInfo.name} showDefaultBackButtonTo="..">
        <Button
          variant="contained"
          startIcon={isLinkJustCopied ? <CheckIcon /> : <LinkIcon />}
          onClick={handleCopyLink}
        >
          {isLinkJustCopied ? "Copied" : "Copy Link"}
        </Button>

        {hasPermissionsToManageVideos && (
          <ActionsMenuButton
            items={[
              {
                label: "Edit Details",
                icon: <EditIcon />,
                onClick: () => setDialogState({ openDialog: "edit" }),
              },
              {
                label: "Delete",
                icon: <DeleteIcon color="error" />,
                onClick: () => setDialogState({ openDialog: "delete" }),
              },
            ]}
          />
        )}
      </GeneralPageHeader>
      <Container maxWidth="lg" disableGutters>
        <Stack spacing={2.5} sx={{ width: "100%", pt: 2.5 }}>
          <Card variant="outlined">
            <CardMedia
              component="video"
              autoPlay
              controls
              controlsList="nodownload"
              onContextMenu={(e) => e.preventDefault()}
              src={fileInfo.downloadUrl}
              sx={{ objectFit: "contain", border: 0, height: 576, bgcolor: "common.black" }}
            />
          </Card>

          <VideoInfoSection videoInfo={videoInfo} allCategories={categories} />
        </Stack>
      </Container>
      {dialogState.openDialog === "edit" && (
        <EditVideoDialog
          videoInfo={videoInfo}
          allCategories={categories}
          onClose={() => setDialogState({})}
          onUpdated={handleUpdated}
        />
      )}
      <DeleteVideoDialog
        videoInfo={videoInfo}
        open={dialogState.openDialog === "delete"}
        onClose={() => setDialogState({})}
        onDeleted={handleDeleted}
      />
    </>
  );
};

export default VideoPage;
